import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Bell, CheckCircle2, RotateCcw, Send, Trash2, User } from "lucide-react";
import axios from "axios";
import { API_BASE, getAuthConfig } from "@/admin/adminApi";
import { createChatSocket } from "@/lib/chatSocket";

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const formatPreviewTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch (err) {
    return "";
  }
};

const conversationLabel = (conversation) => conversation.visitor_name || `Visitor #${conversation.id.slice(0, 6)}`;

const ChatTab = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [pushStatus, setPushStatus] = useState("");
  const [pushBusy, setPushBusy] = useState(false);
  const socketRef = useRef(null);
  const listRef = useRef(null);
  const selectedIdRef = useRef(null);

  const loadConversations = () => {
    axios
      .get(`${API_BASE}/chat/admin/conversations`, getAuthConfig())
      .then((res) => setConversations(res.data))
      .catch(() => setConversations([]));
  };

  useEffect(() => {
    loadConversations();

    socketRef.current = createChatSocket();
    socketRef.current.on("connect", () => {
      socketRef.current.emit("join_room", { room_id: "admin" });
    });
    socketRef.current.on("chat_message", (msg) => {
      if (msg.visitor_id === selectedIdRef.current) {
        setMessages((prev) => (prev.some((item) => item.id === msg.id) ? prev : [...prev, msg]));
      }
      setConversations((prev) => {
        const isOpen = msg.visitor_id === selectedIdRef.current;
        const existing = prev.find((c) => c.id === msg.visitor_id);
        const updated = {
          id: msg.visitor_id,
          visitor_name: existing?.visitor_name || null,
          visitor_email: existing?.visitor_email || null,
          last_message: msg.text,
          last_message_at: msg.created_at,
          unread: msg.sender === "visitor" && !isOpen,
          status: msg.sender === "visitor" ? "open" : existing?.status || "open",
        };
        const rest = prev.filter((c) => c.id !== msg.visitor_id);
        return [updated, ...rest].sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
      });
    });

    return () => {
      if (socketRef.current?.connected) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  const openConversation = (visitorId) => {
    setSelectedId(visitorId);
    axios
      .get(`${API_BASE}/chat/admin/conversations/${visitorId}/messages`, getAuthConfig())
      .then((res) => setMessages(res.data))
      .catch(() => setMessages([]));
    setConversations((prev) => prev.map((c) => (c.id === visitorId ? { ...c, unread: false } : c)));
  };

  const handleSend = async (event) => {
    event.preventDefault();
    const value = text.trim();
    if (!value || sending || !selectedId) {
      return;
    }
    setSending(true);
    setText("");
    try {
      const res = await axios.post(
        `${API_BASE}/chat/admin/conversations/${selectedId}/messages`,
        { text: value },
        getAuthConfig()
      );
      setMessages((prev) => (prev.some((item) => item.id === res.data.id) ? prev : [...prev, res.data]));
    } catch (err) {
      setText(value);
    }
    setSending(false);
  };

  const toggleConversationStatus = async () => {
    if (!selectedId || !selectedConversation) {
      return;
    }
    const nextStatus = selectedConversation.status === "closed" ? "open" : "closed";
    try {
      await axios.patch(
        `${API_BASE}/chat/admin/conversations/${selectedId}/status`,
        { status: nextStatus },
        getAuthConfig()
      );
      setConversations((prev) => prev.map((c) => (c.id === selectedId ? { ...c, status: nextStatus } : c)));
    } catch (err) {
      // Leave state unchanged on failure
    }
  };

  const deleteConversation = async () => {
    if (!selectedId) {
      return;
    }
    if (!window.confirm("Delete this conversation and all its messages? This cannot be undone.")) {
      return;
    }
    try {
      await axios.delete(`${API_BASE}/chat/admin/conversations/${selectedId}`, getAuthConfig());
      setConversations((prev) => prev.filter((c) => c.id !== selectedId));
      setSelectedId(null);
      setMessages([]);
    } catch (err) {
      // Leave state unchanged on failure
    }
  };

  const enablePush = async () => {
    setPushBusy(true);
    setPushStatus("");
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        throw new Error("Push notifications are not supported in this browser");
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error("Notification permission was not granted");
      }
      const registration = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      const { data } = await axios.get(`${API_BASE}/push/public-key`);
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });

      await axios.post(`${API_BASE}/push/subscribe`, { subscription: subscription.toJSON() }, getAuthConfig());
      setPushStatus("Push notifications enabled on this device");
    } catch (err) {
      setPushStatus(err.message || "Could not enable push notifications");
    }
    setPushBusy(false);
  };

  const selectedConversation = conversations.find((c) => c.id === selectedId);

  return (
    <div className="af-chat" data-testid="admin-tab-content-chat">
      <div className="af-chat-pushbar">
        <button
          type="button"
          className="af-btn af-btn-primary"
          onClick={enablePush}
          disabled={pushBusy}
          data-testid="chat-enable-push-btn"
        >
          <Bell size={13} />
          {pushBusy ? "Enabling..." : "Enable Push Notifications"}
        </button>
        <span className="af-hint" data-testid="chat-push-hint">
          Android: works directly in Chrome. iPhone: open this site in Safari, tap Share then Add to Home Screen, launch it from there, then enable notifications.
        </span>
        {pushStatus && <span className="af-media-message" data-testid="chat-push-status">{pushStatus}</span>}
      </div>

      <div className={`af-chat-body ${selectedId ? "af-chat-body-open" : ""}`}>
        <div className="af-chat-list" data-testid="chat-conversation-list">
          {conversations.length === 0 ? (
            <p className="af-hint-block" data-testid="chat-conversation-empty">
              No conversations yet. Visitor messages will appear here.
            </p>
          ) : (
            conversations.map((conversation) => (
              <button
                type="button"
                key={conversation.id}
                className={`af-chat-item ${selectedId === conversation.id ? "af-chat-item-active" : ""}`}
                onClick={() => openConversation(conversation.id)}
                data-testid={`chat-conversation-item-${conversation.id}`}
              >
                <span className="af-chat-item-icon">
                  <User size={14} />
                </span>
                <span className="af-chat-item-body">
                  <span className="af-chat-item-name">{conversationLabel(conversation)}</span>
                  {conversation.visitor_email && (
                    <span className="af-chat-item-email">{conversation.visitor_email}</span>
                  )}
                  <span className="af-chat-item-preview">
                    {conversation.status === "closed" ? "Closed \u00b7 " : ""}
                    {conversation.last_message}
                  </span>
                </span>
                <span className="af-chat-item-meta">
                  <span className="af-chat-item-time">{formatPreviewTime(conversation.last_message_at)}</span>
                  {conversation.unread && <span className="af-chat-item-dot" data-testid={`chat-unread-dot-${conversation.id}`} />}
                </span>
              </button>
            ))
          )}
        </div>

        <div className="af-chat-thread" data-testid="chat-thread-panel">
          {!selectedId ? (
            <p className="af-hint-block" data-testid="chat-thread-empty">
              Select a conversation to view messages.
            </p>
          ) : (
            <>
              <div className="af-chat-thread-head">
                <button
                  type="button"
                  className="af-chat-back"
                  onClick={() => setSelectedId(null)}
                  data-testid="chat-back-btn"
                  aria-label="Back to conversations"
                >
                  <ArrowLeft size={16} />
                </button>
                <span className="af-chat-thread-head-info">
                  <span>{selectedConversation ? conversationLabel(selectedConversation) : "Conversation"}</span>
                  {selectedConversation?.visitor_email && (
                    <span className="af-chat-thread-head-email">{selectedConversation.visitor_email}</span>
                  )}
                </span>
                <span className="af-chat-thread-actions">
                  <button
                    type="button"
                    className="af-btn"
                    onClick={toggleConversationStatus}
                    data-testid="chat-toggle-status-btn"
                  >
                    {selectedConversation?.status === "closed" ? (
                      <>
                        <RotateCcw size={12} />
                        Reopen
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={12} />
                        Close
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="af-btn af-btn-danger"
                    onClick={deleteConversation}
                    data-testid="chat-delete-btn"
                  >
                    <Trash2 size={12} />
                    Delete
                  </button>
                </span>
              </div>
              <div className="af-chat-thread-messages" ref={listRef} data-testid="chat-thread-messages">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={msg.sender === "admin" ? "chat-bubble chat-bubble-admin" : "chat-bubble chat-bubble-visitor"}
                    data-testid={`chat-thread-message-${msg.id}`}
                  >
                    <p>{msg.text}</p>
                  </div>
                ))}
              </div>
              <form className="af-chat-reply" onSubmit={handleSend}>
                <input
                  type="text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type a reply..."
                  data-testid="chat-reply-input"
                />
                <button type="submit" className="af-btn af-btn-primary" disabled={sending} data-testid="chat-reply-send-btn">
                  <Send size={14} />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatTab;
